import { enumsTask } from "./enums";

const ENUMS = {
  minValue: 0,
};

/**
 * Checks if a value is null or undefined.
 * @param {*} value - The value to be checked.
 * @returns {boolean} - True if the value is null or undefined, false otherwise.
 */
export const isNullOrUndefined = (value) => {
  if (value === null || value === undefined || value === "" || value === "undefined") {
    return true;
  } else {
    return false;
  }
};

/**
 * Checks if an array has a length greater than zero and meets a minimum value requirement.
 * @param {Array} value - The array to be checked.
 * @returns {boolean} - True if the array has a length greater than zero and meets the minimum value requirement, false otherwise.
 */
export const arrayGreaterThanZero = (value) => {
  if (value === null || value === undefined || value === "" || value.length === ENUMS.minValue) {
    return false;
  } else {
    return true;
  }
};

/**
 * Parses a string representation of an array and converts it to a JavaScript array.
 * This function attempts to parse a string that represents an array where
 * the array elements are enclosed in single quotes and replaces the single quotes
 * with double quotes to make it a valid JSON string before parsing.
 * @param {string} arrayString - The string representation of an array to be parsed.
 * @returns {Array} - The parsed array. If parsing fails, an empty array is returned.
 */
const parseFilesList = (arrayString) => {
  try {
    // eslint-disable-next-line quotes
    return JSON.parse(arrayString.replace(/'/g, '"'));
  } catch (error) {
    return [];
  }
};

/**
 * Extracts an array from a given string and returns the array along with the strings before and after the array.
 * Throws an error if the input is not a string.
 * @param {string} detailMessageString - The input string containing an array.
 * @returns {object} An object containing the strings before the array, the extracted array, and the strings after the array.
 */
export const extractArrayFromString = (detailMessageString) => {
  if (typeof detailMessageString !== "string") {
    return { beforeArray: "", filesList: [], afterArray: "" };
  }
  const matchFilesArray = detailMessageString.match(/\[[^[\]]*\]/);
  if (matchFilesArray) {
    const arrayString = matchFilesArray[0];
    const beforeArray = detailMessageString.slice(0, matchFilesArray.index);
    const afterArray = detailMessageString.slice(matchFilesArray.index + arrayString.length);
    const filesList = parseFilesList(arrayString);
    return { beforeArray, filesList, afterArray };
  }
  return { beforeArray: detailMessageString, filesList: [], afterArray: "" };
};

/**
 * Checks if the provided file URL is not empty.
 * @param {string} file - The file URL to check.
 * @returns {boolean} Returns `true` if the file URL is not an empty string, otherwise `false`.
 */
export const hashFileUrl = (file) => {
  if (file !== "") {
    return true;
  }
  return false;
};

/**
 * Compares two data objects to determine if they have changed.
 * @param {Object} initialData - The initial data object.
 * @param {Object} currentData - The current data object.
 * @returns {boolean} - Returns true if the data has changed, otherwise false.
 */
export const hasDataChanged = (initialData, currentData) => {
  return JSON.stringify(initialData) !== JSON.stringify(currentData);
};

/**
 * Handles the key press event for a user input.
 * @param {KeyboardEvent} eventUser - The key press event object.
 */
export const handleOnKeyPress = (eventUser) => {
  if (eventUser.key === "Enter") {
    eventUser.preventDefault();
  }
};

/**
 * Extracts the process status from a given pathname and maps it
 * to its corresponding enum value. The function looks for a segment
 * in the URL that matches the pattern `process-{status}`.
 * Supported statuses:
 * - "complete"      → enumsTask.COMPLETED
 * - "inProgress"    → enumsTask.IN_PROGRESS
 * - "interrupted"   → enumsTask.INTERRUPTED
 * If the pathname does not contain a valid status or the extracted
 * status is not recognized, the function returns `null`.
 * @param {string} pathname - The URL pathname from which the process
 *                            status should be extracted.
 * @returns {string|null} The mapped task status from `enumsTask` if
 *                        recognized; otherwise, `null`.
 */
export const getStatusFromPath = (pathname) => {
  const match = pathname.match(/process-(\w+)/);

  let processState = null;

  if (match !== null) {
    processState = match[1];
  }

  if (processState === "complete") {
    return enumsTask.COMPLETED;
  }

  if (processState === "inProgress") {
    return enumsTask.IN_PROGRESS;
  }

  if (processState === "interrupted") {
    return enumsTask.INTERRUPTED;
  }

  return null;
};

/**
 * Normalizes a parameter value by converting string representations
 * of "null" or "undefined" into a real null value.
 * @param {*} value - The value to normalize. It can be of any type, including strings.
 * @returns {*} Returns null if the value is the string "null" or "undefined",
 * otherwise returns the original value.
 */
export const normalizeParam = (value) => {
  if (value === "null" || value === "undefined") {
    return null;
  }
  return value;
};

/**
 * Normalizes a parameter name based on predefined business rules.
 * If the provided value is not null or undefined and matches the string
 * "free process", it will be converted to its localized Spanish label
 * "Proceso libre".
 * @param {string|null|undefined} value The value to normalize. It can be a string, null, or undefined.
 * @returns {string|null|undefined}
 * Returns the normalized parameter value. If the value equals
 * "free process", it returns "Proceso libre"; otherwise, it returns
 * the original value unchanged.
 */
export const normalizeParamName = (value) => {
  if (isNullOrUndefined(value) === false && value === "free process") {
    return "Proceso libre";
  }
  return value;
};
